import type { DataItemId } from '@pn/core/domain/data';
import { formatDataType, type DataItem } from '@pn/core/domain/data';
import {
  isAnnotationSource,
  type FilterProperty,
  type Layer,
} from '@pn/core/domain/layer';
import { areFiltersApplied, type Query } from '@pn/core/domain/query';
import type { ISODateTimeString } from '@pn/core/domain/types';
import type { ProjectUser } from '@pn/core/domain/user';
import type { LibraryFolder } from '@pn/core/domain/workspace';
import { getMapDataItems } from '@pn/core/operations/workspace/mapData';
import { assert } from '@pn/core/utils/assert';
import { hasKeyWithType } from '@pn/core/utils/logic';
import { findReferencedListIds } from '@pn/services/api/query/listReferenceSql';
import {
  getCanvasBBox,
  REFERENCE_PT,
  REFERENCE_ZOOM,
  toBBox,
} from '@pn/services/drawing';
import { getBBox } from '@pn/services/utils/geojson';
import isEqual from 'fast-deep-equal';
import { isEmpty, isNil, isObject, isString } from 'lodash-es';

export type ApiDataSource = {
  type: 'api';
  source: 'postgres' | 'parquet';
  url: string;
  requiredMapDataFields: string[];
  requiredTableDataFields: string[];
};
export type GeoJsonDataSource =
  | { type: 'geojson'; url: string }
  | { type: 'geojson'; data: GeoJSON.FeatureCollection };
export type NoDataSource = {
  type: 'none';
};

type BaseWorkspaceItem = WorkspaceItemInternalState & {
  itemType: 'layer' | 'list' | 'annotation' | 'drawing';
  folder: LibraryFolder | undefined;
  isTemporary: boolean;
  temporarySourceItemId?: WorkspaceItem['id']; // tracks the origin of a temporary item
  id: string;
  dataType: string;
  numberOfElements: number | undefined;
  colorIndicator?: string;
  name: string;
  description?: string;
  origin: 'pn' | 'stackdx' | 'boe_intel' | 'arcgis';
  originUrl?: string;
  createdAt: ISODateTimeString;
  updatedAt: ISODateTimeString;
  createdBy: ProjectUser | undefined;
  isShared: boolean;
  isGlobal: boolean;
  map: {
    layers: Layer[];
    originalLayers?: Layer[];
    filterProperty?: FilterProperty;
  };
  dataSource: ApiDataSource | GeoJsonDataSource | NoDataSource;
  detailsSource: 'api' | 'local';
  query: Query;
  country?: 'canada' | 'usa';
  module?: 'background' | 'grids';
  metadata?: {
    isNonInteractive?: boolean; // no Save button, no crosshair pointer, no map clicks
    isQueryArea?: boolean;
    dataOriginDescription?: string;
  };
};

export type WorkspaceItemInternalState = {
  isVisible: boolean;
  isProcessed: boolean;
  isRendered: boolean;
  isError: boolean;
  errorMessage?: string; // error reported by the data source, if any

  isLoaded: boolean; // indicates if `query.scopeIds` has been fetched

  totalCount: number;
  streamedCount: number;

  tableDataItems: DataItem[];
};

export type WorkspaceItem = BaseWorkspaceItem & {
  sourceItem?: BaseWorkspaceItem;
};

export function isWorkspaceItem(arg: unknown): arg is WorkspaceItem {
  return isObject(arg) && hasKeyWithType(arg, 'itemType', isString);
}

export function getDataItemSelected(
  item: WorkspaceItem,
  id: DataItemId | undefined
): DataItem | undefined {
  return item.tableDataItems.find((dataItem) => dataItem._id === id);
}

export function isNonStreamableItem(item: WorkspaceItem): boolean {
  return isNil(item.sourceItem) && item.dataSource.type === 'api';
}
export function isStreamableItem(item: WorkspaceItem): boolean {
  return !isNil(item.sourceItem) && item.dataSource.type === 'api';
}

/**
 * SQL and area clauses are evaluated by the API. Items processed client-side
 * (GeoJSON sources, see `processByQuery`) only support filters.
 */
export function supportsSqlAndAreaClauses(item: WorkspaceItem): boolean {
  return item.dataSource.type === 'api';
}

export function isEmptyWorkspaceItem(item: WorkspaceItem): boolean {
  return (
    !isNil(item.sourceItem) &&
    isEmpty(item.query.scopeIds) &&
    isEmpty(item.query.filters) &&
    isNil(item.query.sql) &&
    isNil(item.query.geometry) &&
    isNil(item.query.sourceListId)
  );
}

export function isDynamicWorkspaceItem(item: WorkspaceItem): boolean {
  return (
    areFiltersApplied(item.query.filters) ||
    !isNil(item.query.sql) ||
    !isNil(item.query.geometry) ||
    !isNil(item.query.sourceListId)
  );
}

export function isSavedDynamicList(
  item: WorkspaceItem | undefined
): item is WorkspaceItem {
  return (
    !isNil(item) &&
    item.itemType === 'list' &&
    !item.isTemporary &&
    isDynamicWorkspaceItem(item)
  );
}

export function isSavedStaticList(
  item: WorkspaceItem | undefined
): item is WorkspaceItem {
  return (
    !isNil(item) &&
    item.itemType === 'list' &&
    !item.isTemporary &&
    !isDynamicWorkspaceItem(item)
  );
}

/**
 * Live filtered layers can't be built on other live filtered layers, and the
 * API rejects SQL that references one, so such an item can only be saved as a
 * snapshot. Such references come from SQL (e.g. AI-generated layers), since
 * `query.sourceListId` is only set for static lists. Lists missing from the
 * workspace can't be checked here; the API still rejects them on save.
 */
export function findReferencedDynamicList(
  item: WorkspaceItem,
  allWorkspaceItems: Record<WorkspaceItem['id'], WorkspaceItem>
): WorkspaceItem | undefined {
  return findReferencedListIds(item.query.sql)
    .map((id) => allWorkspaceItems[id] as WorkspaceItem | undefined)
    .find(isSavedDynamicList);
}

export type SaveMode = 'static' | 'dynamic';

export function getNumberOfIds(item: WorkspaceItem): number {
  return isDynamicWorkspaceItem(item)
    ? getMapDataItems(item).length
    : /* Blank rows in a pasted id list produce empty-string entries. */
      item.query.scopeIds.filter((id) => id.toString() !== '').length;
}

export function getFormattedItemName(item: WorkspaceItem | undefined): string {
  if (isNil(item)) return 'N/A';

  if (!isEmpty(item.name)) return item.name;

  const { scopeIds, filters } = item.query;

  if (!isEmpty(scopeIds) && isEmpty(filters)) {
    return `Selection of ${formatDataType(item.dataType)}`;
  } else if (isEmpty(scopeIds) && !isEmpty(filters)) {
    return `Filtered ${formatDataType(item.dataType)}`;
  } else if (!isEmpty(scopeIds) && !isEmpty(filters)) {
    return `Filtered selection of ${formatDataType(item.dataType)}`;
  } else {
    return `New ${item.itemType === 'drawing' ? 'annotation' : 'layer'} (unsaved)`;
  }
}

export function getItemColor(item: WorkspaceItem | undefined): unknown {
  if (isNil(item)) return 'transparent';
  if (!isNil(item.colorIndicator)) return item.colorIndicator;

  return item.map.layers[0].style.color;
}

export function getItemBBox(item: WorkspaceItem): GeoJSON.BBox | undefined {
  switch (item.itemType) {
    case 'annotation':
      assert(
        isAnnotationSource(item.map.layers[0].source),
        'Invalid annotation source'
      );

      return getBBox(
        item.map.layers[0].source.data.features.map((f) => f.geometry)
      );
    case 'drawing': {
      const layer = item.map.layers[0];
      assert(layer.metadata?.type === 'drawing', 'Invalid drawing layer');
      if (isEmpty(layer.metadata.features)) return undefined;

      const canvasBBox = getCanvasBBox(layer.metadata.features);
      return toBBox(canvasBBox, REFERENCE_PT, REFERENCE_ZOOM);
    }
    default: {
      const dataItems = getMapDataItems(item);
      const geometries = dataItems
        .map((item) => item.geometry)
        .filter((g) => !isNil(g));
      return isEmpty(geometries) ? undefined : getBBox(geometries);
    }
  }
}

export function isOverridden(item: WorkspaceItem): boolean {
  return (
    !isNil(item.map.originalLayers) &&
    !isEqual(item.map.layers, item.map.originalLayers)
  );
}

/* PORTS */

export interface IWorkspaceItemMapper<T, V = T> {
  toWorkspaceItem: (item: T, sourceItem?: WorkspaceItem) => WorkspaceItem;
  toOriginalItem: (item: WorkspaceItem) => V;
}

/**
 * Returns the items above `itemId` in a top-to-bottom list of IDs, nearest
 * first.
 */
export function getItemsAbove(params: {
  orderedIds: WorkspaceItem['id'][];
  itemId: WorkspaceItem['id'];
  workspaceItems: Record<WorkspaceItem['id'], WorkspaceItem>;
}): WorkspaceItem[] {
  const { orderedIds, itemId, workspaceItems } = params;

  return orderedIds
    .slice(0, orderedIds.indexOf(itemId))
    .reverse()
    .flatMap((id) => workspaceItems[id] ?? []);
}
